import React, { useState } from "react";
import { GhostButton, RowList } from "@/shared/components/settings-atoms";
import ProviderMark from "@/shared/components/models/ProviderMark";
import { PROVIDERS, type ModelOption, type ModelsWorkspace, type ProviderId } from "@/shared/types/models";

const PAGE_SIZE = 30;
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 3 }).format;
const tokens = (n: number) => (n >= 1_000_000 ? `${+(n / 1_000_000).toFixed(1)}M` : `${Math.round(n / 1000)}K`);

/** What the workspace's connections can run. Every row has a price: a model the
    backend has no price for is never listed, so nothing here is a guessed $0. */
export default function ModelCatalog({ data }: { data: ModelsWorkspace }) {
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("all");
  const [page, setPage] = useState(0);
  const providers = (Object.keys(PROVIDERS) as ProviderId[]).filter((id) => data.models.some((m) => m.provider === id));
  const activeProvider = providers.some((id) => id === provider) ? provider : "all";
  const query = search.trim().toLowerCase();
  const filtered = data.models.filter((model) => (activeProvider === "all" || model.provider === activeProvider)
    && `${model.id} ${model.name} ${model.family}`.toLowerCase().includes(query));
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const field = "h-8 min-w-0 px-2.5 text-[13px] rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] outline-none focus:ring-[var(--hairline-focus)]";

  if (!data.models.length) {
    return <p className="text-[13px] text-[var(--text-secondary)]">{data.connections.length ? "Your connections have no models we can run yet." : "Add a connection to see the models it can run."}</p>;
  }
  return <div className="space-y-3">
    <div className="flex flex-wrap gap-2">
      <input aria-label="Search models" placeholder="Search by name or model ID…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className={`${field} flex-1 basis-[200px]`} />
      <select aria-label="Filter by provider" value={activeProvider} onChange={(e) => { setProvider(e.target.value); setPage(0); }} className={field}>
        <option value="all">All providers</option>
        {providers.map((id) => <option key={id} value={id}>{PROVIDERS[id]}</option>)}
      </select>
    </div>
    <p role="status" className="text-[12px] text-[var(--text-secondary)]"><span className="font-mono tabular-nums">{filtered.length}</span> models{query || activeProvider !== "all" ? <> of <span className="font-mono tabular-nums">{data.models.length}</span></> : ""} · USD per 1M tokens, input / output</p>
    <RowList>
      {visible.map((model) => {
        const connections = data.connections.filter((c) => c.provider === model.provider && c.modelIds.includes(model.id));
        return <ModelRow key={`${model.provider}:${model.id}`} model={model} via={`${PROVIDERS[model.provider]} · ${connections.map((c) => c.name).join(", ")}`} />;
      })}
      {!visible.length && <p className="py-4 text-[13px] text-[var(--text-secondary)]">No models match these filters.</p>}
    </RowList>
    {pages > 1 && <div className="flex items-center justify-between gap-2">
      <span className="text-[12px] text-[var(--text-secondary)]">Page <span className="font-mono tabular-nums">{currentPage + 1} / {pages}</span></span>
      <div className="flex gap-2"><GhostButton disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</GhostButton><GhostButton disabled={currentPage === pages - 1} onClick={() => setPage(currentPage + 1)}>Next</GhostButton></div>
    </div>}
  </div>;
}

/** Definable's own models: what a workspace can run with no connection. */
export function DefinableModels({ models }: { models: ModelOption[] }) {
  // by the model's maker, never the account Definable runs it on
  return <RowList>{models.map((model) => <ModelRow key={`${model.provider}:${model.id}`} model={model} via={PROVIDERS[model.provider]} />)}</RowList>;
}

/** One model: its mark, name and id, where it runs, its features, price and window. */
function ModelRow({ model, via }: { model: ModelOption; via: string }) {
  // On a phone the price drops under the name, so names and ids keep the row's width
  return <div className="grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 py-3 text-[13px]">
    <ProviderMark provider={model.provider} />
    <div className="min-w-0">
      <p className="font-medium truncate" title={model.name}>{model.name}</p>
      <p className="font-mono text-[11.5px] text-[var(--text-secondary)] truncate" title={model.id}>{model.id}</p>
      <p className="text-[12px] text-[var(--text-secondary)] truncate" title={via}>{via}</p>
      {!!model.capabilities.length && <p className="text-[12px] text-[var(--text-tertiary)] truncate">{model.capabilities.join(" · ")}</p>}
    </div>
    <div className="col-start-2 sm:col-start-auto sm:text-right text-[12px] text-[var(--text-secondary)]">
      <p className="font-mono tabular-nums text-[var(--text-primary)]">{usd(model.price.input)} / {usd(model.price.output)}</p>
      <p><span className="font-mono tabular-nums">{tokens(model.contextWindow)}</span> context</p>
      {model.deprecated && <p>Deprecated</p>}
    </div>
  </div>;
}
